import React, { useMemo } from "react";
import { Col, Row } from "react-bootstrap";

import { LineDivider } from "components/layout";
import { useBackendPlatform } from "hooks";
import { backendPlatformsWithFrontendPlatforms } from "mappers";
import { ProgramDataType } from "types";
import {
	ConnectionPrimaryTypes,
	OrderNumberTypes,
	TerminalConfigurationOption,
	TerminalTypes,
} from "types/enums";
import { float2Normalizer, float6Normalizer } from "validators";

import TokenizationView from "./tokenizationView";
import {
	AccessoriesView,
	FileBuildView,
	ProgramView,
	TerminalOptionsView,
	TransarmorView,
} from "..";
import TextBlock from "../components/textBlock";

import stylesLayout from "../../../../components/layout/layout.module.scss";

type TerminalValues = {
	terminalConfigurationOption: string;
	terminalType: { [key: string]: any };
	manufacturer: { [key: string]: any };
	otherManufacturerName: string;
	model: { [key: string]: any };
	otherModelName: string;
	pricePerTerminal: string;
	quantity: string;
	cloverTerminalSettings: { [key: string]: any };
	terminalAccessories: string;
	terminalOptions: { [key: string]: any };
	industryType: string;
	connectionPrimary: string;
	connectionBackup: string;
	isAutoCloseEnabled: string;
	autoCloseAtHours: string;
	autoCloseAtMinutes: string;
	autoCloseAtMedian: { [key: string]: any };
	isTransarmorEnabled: string;
	transarmorMonthlyFee: string;
	transarmorPerTransactionFee: string;
	isTsysTokenizationEnabled: string;
	monthRentalPrice: string;
	equipmentInsurance: string;
	shippingAndHandling: string;
	estimatedTax: string;
	stateTax: string;
};

type TerminalInfoViewProps = {
	terminal: TerminalValues;
	fileBuild: any;
	index: number;
	data: ProgramDataType;
};

const TerminalView = ({
	terminal,
	fileBuild,
	index,
	data,
}: TerminalInfoViewProps) => {
	const {
		terminalConfigurationOption,
		terminalType,
		manufacturer,
		otherManufacturerName,
		model,
		otherModelName,
		pricePerTerminal,
		quantity,
		cloverTerminalSettings,
		terminalAccessories,
		terminalOptions,
		industryType,
		connectionPrimary,
		connectionBackup,
		isAutoCloseEnabled,
		autoCloseAtHours,
		autoCloseAtMinutes,
		autoCloseAtMedian,
		monthRentalPrice,
		equipmentInsurance,
		shippingAndHandling,
		estimatedTax,
		stateTax,
	} = terminal;

	const isCloverManufacturer = useMemo(
		() => manufacturer?.name === "Clover",
		[manufacturer?.name],
	);

	const isOtherManufacturer = useMemo(
		() => manufacturer?.name === "Other",
		[manufacturer?.name],
	);

	const isOtherModel = useMemo(() => {
		return model?.name === "Other";
	}, [model?.name]);

	const isCloverFDCloverGoNFCRDRV2Model = useMemo(
		() => model?.name === "FDR Clover Go NFC RDR V2",
		[model?.name],
	);

	const isCloverStationSoloBundleWithoutCashDrawerModel = useMemo(
		() => model?.name === "Clover Station Solo Bundle without Cash Drawer",
		[model?.name],
	);

	const isCloverStationDuoBundleWithCashDrawerModel = useMemo(
		() => model?.name === "Clover Station Duo Bundle with Cash Drawer",
		[model?.name],
	);

	const isCloverStationDuoBundleWithoutCashDrawerModel = useMemo(
		() => model?.name === "Clover Station Duo Bundle without Cash Drawer",
		[model?.name],
	);

	const isCloverStationSoloBundleWithCashDrawerModel = useMemo(
		() => model?.name === "Clover Station Solo Bundle with Cash Drawer",
		[model?.name],
	);

	const isCloverStationSoloTabletModel = useMemo(
		() => model?.name === "Clover Station Solo Tablet",
		[model?.name],
	);

	const isCloverStationModel = useMemo(
		() => model?.name === "Station",
		[model?.name],
	);

	const isNewRental =
		terminalConfigurationOption ===
		TerminalConfigurationOption.NewRental.name;

	const isAutoClose = isAutoCloseEnabled === "true" ? true : false;

	const manufacturerName = manufacturer?.name
		? `- ${manufacturer?.name}`
		: "";
	const modelName = model?.name ? `- ${model?.name}` : "";

	const {
		isBackEndPlatformOmaha,
		isBackEndPlatformNorth,
		isBackEndPlatformTsys,
	} = useBackendPlatform(data?.backEndPlatform);

	const displayFrontEndPlatform =
		backendPlatformsWithFrontendPlatforms.includes(data?.backEndPlatform);

	return (
		<div data-autotest={`TerminalSection-${index + 1}`}>
			<TextBlock
				label={`Terminal ${index + 1} ${manufacturerName} ${modelName}`}
				value={
					TerminalConfigurationOption.getByName(
						terminalConfigurationOption,
					)?.displayName
				}
				dataAutotest="terminalName"
			/>
			<ProgramView
				data={data}
				displayFrontEndPlatform={displayFrontEndPlatform}
			/>
			<Row>
				<Col xl={3}>
					<TextBlock
						label="Ownership"
						value={
							TerminalTypes.getByValue(terminalType?.value)
								?.displayName
						}
						dataAutotest="ownership"
					/>
				</Col>
				<Col xl={3}>
					<TextBlock
						label="Manufacturer"
						value={manufacturer?.name}
						dataAutotest="manufacturer"
					/>
				</Col>
				<Col xl={6}>
					<TextBlock
						label="Model"
						value={model?.name}
						dataAutotest="model"
					/>
				</Col>
			</Row>
			{(isOtherManufacturer || isOtherModel) && (
				<Row>
					{isOtherManufacturer && (
						<Col xl={6}>
							<TextBlock
								label="Manufacturer Name"
								value={otherManufacturerName}
								dataAutotest="manufacturerName"
							/>
						</Col>
					)}
					{isOtherModel && (
						<Col xl={6}>
							<TextBlock
								label="Model Name"
								value={otherModelName}
								dataAutotest="model"
							/>
						</Col>
					)}
				</Row>
			)}
			{isNewRental && (
				<>
					<Row>
						<Col xl={3}>
							<TextBlock
								label="Rental Price (per month)"
								value={
									float6Normalizer(monthRentalPrice, "$") ||
									"$0.00000"
								}
								dataAutotest="monthRentalPrice"
							/>
						</Col>
						<Col xl={3}>
							<TextBlock
								label="Equipment Insurance"
								value={
									float6Normalizer(equipmentInsurance, "$") ||
									"$0.00000"
								}
								dataAutotest="equipmentInsurance"
							/>
						</Col>
						<Col xl={6}>
							<TextBlock
								label="Shipping and Handling (one time)"
								value={
									float6Normalizer(
										shippingAndHandling,
										"$",
									) || "$0.00000"
								}
								dataAutotest="shippingAndHandling"
							/>
						</Col>
					</Row>
					<Row>
						<Col xl={3}>
							<TextBlock
								label="Estimated Tax"
								value={
									float6Normalizer(estimatedTax, "$") ||
									"$0.00000"
								}
								dataAutotest="estimatedTax"
							/>
						</Col>
						<Col xl={3}>
							<TextBlock
								label="State tax"
								value={
									float6Normalizer(stateTax, "", "%") ||
									"0.00000"
								}
								dataAutotest="stateTax"
							/>
						</Col>
					</Row>
				</>
			)}

			<Row>
				{!isNewRental && (
					<Col xl={3}>
						<TextBlock
							label="Price Per Terminal"
							value={
								float2Normalizer(pricePerTerminal, "$") ||
								"$0.00"
							}
							dataAutotest="pricePerTerminal"
						/>
					</Col>
				)}

				<Col xl={3}>
					<TextBlock
						label="Quantity"
						value={quantity}
						dataAutotest="terminalQuantity"
					/>
				</Col>
			</Row>
			{isCloverManufacturer && (
				<Row>
					{(isCloverStationSoloBundleWithoutCashDrawerModel ||
						isCloverStationDuoBundleWithoutCashDrawerModel ||
						isCloverStationDuoBundleWithCashDrawerModel ||
						isCloverStationSoloBundleWithCashDrawerModel ||
						isCloverStationSoloTabletModel ||
						isCloverStationModel) && (
						<Col xl={3}>
							<TextBlock
								label="Clover Services (Per Device), Fee"
								value={
									float6Normalizer(
										cloverTerminalSettings?.servicesPerDeviceFee,
										"$",
									) || "$0.000000"
								}
								dataAutotest="cloverServicesFee"
							/>
						</Col>
					)}

					{isCloverFDCloverGoNFCRDRV2Model && (
						<Col xl={3}>
							<TextBlock
								label="Clover Go Monthly, Fee"
								value={
									float6Normalizer(
										cloverTerminalSettings?.goMonthlyFee,
										"$",
									) || "$0.000000"
								}
								dataAutotest="cloverGoMonthlyFee"
							/>
						</Col>
					)}
				</Row>
			)}
			{(isBackEndPlatformOmaha || isBackEndPlatformNorth) && (
				<TransarmorView data={terminal} />
			)}
			{isBackEndPlatformTsys && <TokenizationView data={terminal} />}
			<LineDivider className={`${stylesLayout.lineDividerHolder}`} />
			{Array.isArray(terminalAccessories) &&
				terminalAccessories?.length > 0 && (
					<AccessoriesView
						terminalAccessories={terminalAccessories}
					/>
				)}

			{Object.values(terminalOptions)?.includes(true) && (
				<TerminalOptionsView terminalOptions={terminalOptions} />
			)}
			<Row>
				<Col xl={3}>
					<TextBlock
						label="Industry Type"
						value={
							OrderNumberTypes?.getByValue(+industryType)
								?.displayName
						}
						dataAutotest="industryType"
					/>
				</Col>
				<Col xl={3}>
					<TextBlock
						label="Connection-Primary"
						value={
							ConnectionPrimaryTypes?.getByValue(
								+connectionPrimary,
							)?.displayName
						}
						dataAutotest="connectionPrimary"
					/>
				</Col>
				<Col xl={6}>
					<TextBlock
						label="Connection-Backup"
						value={
							ConnectionPrimaryTypes?.getByValue(
								+connectionBackup,
							)?.displayName
						}
						dataAutotest="connectionBackup"
					/>
				</Col>
			</Row>
			{isAutoClose && (
				<Row>
					<Col xl={6}>
						<TextBlock
							label="Autoclose"
							value={`${autoCloseAtHours || "-"}:${
								autoCloseAtMinutes || "-"
							} ${autoCloseAtMedian?.name}`}
							dataAutotest="autoclose"
						/>
					</Col>
				</Row>
			)}
			<LineDivider className={`${stylesLayout.lineDividerHolder}`} />
			<h6
				className={`${stylesLayout.viewSectionTitle} ${stylesLayout.subTitle}`}
			>
				File Build Information
			</h6>
			<FileBuildView fileBuild={fileBuild} />
		</div>
	);
};

export default TerminalView;
